import { useState, useEffect } from 'react';
import { Mail, Trash2, Loader2, ExternalLink } from 'lucide-react';
import adminApi from '@/lib/adminApi';

export default function AdminContacts() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      const response = await adminApi.get('/api/admin/contacts');
      setContacts(response.data);
    } catch (error) {
      console.error('Failed to fetch contacts:', error);
      /* list stays empty when the fetch fails */
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (contactId) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;
    
    setDeleting(contactId);
    
    try {
      await adminApi.delete(`/api/admin/contacts/${contactId}`);
      setContacts(contacts.filter(c => c.id !== contactId));
      if (selectedContact?.id === contactId) {
        setSelectedContact(null);
      }
    } catch (error) {
      alert('Failed to delete message');
    } finally {
      setDeleting(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="admin-contacts">
      <h1 className="text-3xl font-bold mb-8">Contact Messages</h1>

      {contacts.length === 0 ? (
        <div className="text-center py-12 border border-[#27272A] bg-[#0C0C0C]">
          <Mail className="w-12 h-12 text-[#52525B] mx-auto mb-4" />
          <p className="text-[#A1A1AA]">No contact messages yet</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Messages List */}
          <div className="border border-[#27272A] bg-[#0C0C0C] overflow-hidden">
            <div className="divide-y divide-[#27272A] max-h-[600px] overflow-y-auto">
              {contacts.map((contact) => (
                <div
                  key={contact.id || `contact-${contacts.indexOf(contact)}`}
                  className={`p-4 cursor-pointer transition-colors hover:bg-[#121212] ${
                    selectedContact?.id === contact.id ? 'bg-[#121212] border-l-2 border-[#007AFF]' : ''
                  }`}
                  onClick={() => setSelectedContact(contact)}
                  data-testid={`contact-item-${contact.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{contact.name}</p>
                      <p className="text-[#007AFF] text-sm truncate">{contact.email}</p>
                      <p className="text-[#A1A1AA] text-sm truncate mt-1">{contact.subject}</p>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <span className="text-[#52525B] text-xs whitespace-nowrap">
                        {new Date(contact.created_at).toLocaleDateString('en-AU')}
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(contact.id);
                        }}
                        disabled={deleting === contact.id}
                        className="p-1 text-[#52525B] hover:text-red-400 transition-colors"
                        title="Delete"
                      >
                        {deleting === contact.id ? (
                          <Loader2 size={16} className="animate-spin" />
                        ) : (
                          <Trash2 size={16} />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Message Detail */}
          <div className="border border-[#27272A] bg-[#0C0C0C] p-6">
            {selectedContact ? (
              <div data-testid="contact-detail">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h2 className="text-xl font-bold">{selectedContact.name}</h2>
                    <a
                      href={`mailto:${selectedContact.email}`}
                      className="text-[#007AFF] hover:underline flex items-center gap-1"
                    >
                      {selectedContact.email}
                      <ExternalLink size={14} />
                    </a>
                  </div>
                  <span className="text-[#52525B] text-sm">
                    {new Date(selectedContact.created_at).toLocaleString('en-AU')}
                  </span>
                </div>

                <div className="mb-6">
                  <p className="text-[#52525B] text-sm mb-1">Subject:</p>
                  <p className="font-medium">{selectedContact.subject}</p>
                </div>

                <div>
                  <p className="text-[#52525B] text-sm mb-2">Message:</p>
                  <div className="p-4 bg-[#050505] border border-[#27272A] text-[#A1A1AA] whitespace-pre-wrap">
                    {selectedContact.message}
                  </div>
                </div>

                <div className="mt-6 flex gap-4">
                  <a
                    href={`mailto:${selectedContact.email}?subject=Re: ${selectedContact.subject}`}
                    className="btn-primary flex items-center gap-2"
                  >
                    <Mail size={18} />
                    Reply
                  </a>
                  <button
                    onClick={() => handleDelete(selectedContact.id)}
                    disabled={deleting === selectedContact.id}
                    className="btn-secondary text-red-400 border-red-400/30 hover:border-red-400 flex items-center gap-2"
                  >
                    {deleting === selectedContact.id ? (
                      <Loader2 size={18} className="animate-spin" />
                    ) : (
                      <Trash2 size={18} />
                    )}
                    Delete
                  </button>
                </div>
              </div>
            ) : (
              <div className="h-full flex items-center justify-center text-[#52525B]">
                Select a message to view details
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
